import { FormikHelpers } from "formik";
import { TFunction } from "i18next";
import { SignInFormValues } from "types/signInForm";
import {
  SignUpFormDto,
  SignUpFormValues,
} from "types/signUpForm";
import { getCountriesOptions, getCountryByCode } from "utils/countries";
import { passwordSchema } from "utils/passwordRules";
import * as yup from "yup";
import type { SignupConfig } from "../../config/schema";
import { toastResponseError } from "../../utils/responseMessageHelper";

const englishOnlyRegExp = /^[\x20-\x7E]*$/i;

export { getCountriesOptions, getCountryByCode };

export const getLocalesOptions = (t: TFunction) =>
  [
    { label: t("locales.ru"), value: "RU" },
    { label: t("locales.en"), value: "en" },
  ].sort((a, b) => a.label.localeCompare(b.label));

export const signUpFormik = (
  t: TFunction,
  handleSignUp: (data: SignUpFormDto) => Promise<any>,
  handleSignIn: (data: SignInFormValues) => Promise<any>,
  redirect: () => void,
  signup: SignupConfig,
  apiPartnerId?: number,
) => ({
  validateOnChange: true,
  validateOnBlur: false,
  initialValues: {
    firstName: "",
    lastName: "",
    email: "",
    signupToken: "",
    password: "",
    rePassword: "",
    terms: false,
    country: { value: "", label: "" },
    locale: getLocalesOptions(t)[0],
  },
  validationSchema: () =>
    yup.object().shape({
      firstName: yup
        .string()
        .min(2, t("error.stringLonger", { length: 2 }))
        .max(30, t("error.stringShorter", { length: 30 }))
        .matches(/^\S*$/, t("error.whiteSpaces"))
        .matches(englishOnlyRegExp, t("error.englishOnly"))
        .required(t("error.required", { field: t("auth.firstName") })),
      lastName: yup
        .string()
        .min(2, t("error.stringLonger", { length: 2 }))
        .max(30, t("error.stringShorter", { length: 30 }))
        .matches(/^\S*$/, t("error.whiteSpaces"))
        .matches(englishOnlyRegExp, t("error.englishOnly"))
        .required(t("error.required", { field: t("auth.lastName") })),
      country: yup.object({
        value: yup
          .string()
          .required(t("error.required", { field: t("auth.country") })),
        label: yup.string(),
      }),
      email: yup
        .string()
        .email(t("error.invalid", { field: t("auth.email") }))
        .matches(englishOnlyRegExp, t("error.englishOnly"))
        .required(t("error.required", { field: t("auth.email") })),
      ...(signup.mode === "open"
        ? {}
        : {
            signupToken: yup
              .string()
              .min(2, t("error.stringLonger", { length: 2 }))
              .max(30, t("error.stringShorter", { length: 30 }))
              .matches(/^\S*$/, t("error.whiteSpaces"))
              .required(t("error.required", { field: t("auth.signupToken") })),
          }),
      password: passwordSchema(t).required(
        t("error.required", { field: t("auth.password") }),
      ),
      rePassword: yup
        .string()
        .oneOf([yup.ref("password")], t("error.invalidRePass"))
        .required(t("error.required", { field: t("auth.rePassword") })),
      terms: yup.bool().oneOf([true], "Terms must be accepted"),
    }),
  onSubmit: (
    values: SignUpFormValues,
    { setSubmitting }: FormikHelpers<SignUpFormValues>,
  ) => {
    const {
      firstName,
      lastName,
      email,
      signupToken,
      password,
      rePassword,
      country,
      locale,
      terms,
    } = values;

    handleSignUp({
      email,
      signup_token: signup.mode === "open" ? signup.partnerToken : signupToken,
      password,
      password_confirmation: rePassword,
      country: country.value ?? "",
      locale: locale.value ?? "",
      accepted_terms_and_conditions: terms,
      first_name: firstName,
      last_name: lastName,
      user_type: "Forwarding",
      ...(apiPartnerId !== undefined ? { partner_id: apiPartnerId } : {}),
    })
      .then(() => {
        //FIXME: delete after completing the task on the BE
        return handleSignIn({ email, password });
      })
      .then(() => {
        redirect();
      })
      .catch((error: Error) => toastResponseError(error))
      .finally(() => setSubmitting(false));
  },
});
